import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { BellRing, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { isPushSupported, subscribePush } from '../../services/pushSubscription.service';

// ---------------------------------------------------------------------------
// Persistence key — once dismissed or granted, never show again
// ---------------------------------------------------------------------------
const DISMISSED_KEY = 'push_prompt_dismissed';

// ---------------------------------------------------------------------------
// PushPermissionPrompt
//
// Shows a non-intrusive banner prompting the user to enable push notifications.
// Guards:
//  - PushManager API must be present (browser support + iOS standalone check)
//  - Notification.permission must be 'default' (not yet decided)
//  - User must not have previously dismissed or already granted permission
//  - Should not render on /settings to avoid duplication with the toggle there
// ---------------------------------------------------------------------------

export default function PushPermissionPrompt() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check all guards synchronously on mount
    if (!isPushSupported()) return;
    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'default') return;
    if (localStorage.getItem(DISMISSED_KEY)) return;
    // Don't show on the settings page — it has its own toggle
    if (window.location.pathname.startsWith('/settings')) return;

    setVisible(true);
  }, []);

  const handleEnable = useCallback(async () => {
    setLoading(true);
    try {
      const subscription = await subscribePush();
      if (subscription) {
        toast.success(t('notifications.push.enabled'));
        setVisible(false);
        localStorage.setItem(DISMISSED_KEY, '1');
        return;
      }
      // subscribePush returned null — disambiguate to give the user real feedback
      // instead of a silent banner stuck in loading state.
      if (Notification.permission === 'denied') {
        // User denied — hide permanently
        toast.error(t('notifications.push.denied'));
        setVisible(false);
        localStorage.setItem(DISMISSED_KEY, '1');
      } else {
        // Permission still 'default': browser silently blocked the prompt
        // (Chrome quiet UI on repeat dismissals, Safari quirks, etc.) or the
        // user dismissed the native dialog without choosing. The banner can't
        // self-heal — surface a toast so the click isn't a black hole.
        toast(t('notifications.push.blocked'), { icon: 'ℹ️' });
      }
    } catch {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const handleDismiss = useCallback(() => {
    localStorage.setItem(DISMISSED_KEY, '1');
    setVisible(false);
  }, []);

  // Also hide if user grants or denies permission outside this component
  useEffect(() => {
    if (!visible) return;
    if (!('permissions' in navigator)) return;

    let cancelled = false;

    void navigator.permissions.query({ name: 'notifications' }).then((status) => {
      const onChange = () => {
        if (cancelled) return;
        if (status.state !== 'prompt') {
          setVisible(false);
          localStorage.setItem(DISMISSED_KEY, '1');
        }
      };
      status.addEventListener('change', onChange);
      // Cleanup handled by the outer useEffect return
      return () => status.removeEventListener('change', onChange);
    });

    return () => {
      cancelled = true;
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      role="banner"
      aria-label={t('notifications.push.prompt.title')}
      className="fixed bottom-20 md:bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-80 z-[var(--z-toast)] animate-fade-in-up"
    >
      <div className="bg-bg-elevated border border-border-default rounded-lg shadow-xl px-4 py-3 flex items-start gap-3">
        {/* Icon */}
        <div className="shrink-0 w-8 h-8 bg-accent-primary/10 rounded-md flex items-center justify-center mt-0.5">
          <BellRing className="w-4 h-4 text-accent-primary" aria-hidden="true" />
        </div>

        {/* Text + CTA */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-text-primary leading-snug">
            {t('notifications.push.prompt.title')}
          </p>
          <p className="text-xs text-text-secondary mt-0.5 leading-snug">
            {t('notifications.push.prompt.body')}
          </p>
          <button
            type="button"
            onClick={handleEnable}
            disabled={loading}
            className="
              mt-2 inline-flex items-center h-7 px-3 rounded-md
              text-xs font-semibold
              bg-accent-primary text-white
              hover:bg-accent-primary/90
              disabled:opacity-60 disabled:cursor-not-allowed
              transition-colors focus-visible:outline-none
              focus-visible:ring-2 focus-visible:ring-accent-primary
            "
          >
            {loading ? t('common.loading') : t('notifications.push.prompt.cta')}
          </button>
        </div>

        {/* Dismiss */}
        <button
          type="button"
          onClick={handleDismiss}
          aria-label={t('notifications.push.prompt.dismiss')}
          className="shrink-0 text-text-tertiary hover:text-text-secondary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary rounded"
        >
          <X className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
